import { z } from "zod";
const HEMISPHERES = ["NORTHERN", "SOUTHERN"];
export const HemisphereSchema = z.enum(HEMISPHERES);
/**
 * 3-letter racing country codes with a Southern-hemisphere nominal
 * racing-age turnover date (Aug 1). Every other code defaults Northern
 * (Jan 1). This list covers the major Southern-hemisphere racing
 * jurisdictions; unmapped codes fall back to Northern per
 * `docs/products/stable.md` and are always overridable by an authorized
 * Stable member.
 */
const SOUTHERN_HEMISPHERE_COUNTRY_CODES = new Set([
    "AUS",
    "NZL",
    "ZAF",
    "ARG",
    "BRA",
    "CHI",
    "URY",
    "PER",
    "ZWE",
]);
export function hemisphereForCountryCode(countryCode) {
    return SOUTHERN_HEMISPHERE_COUNTRY_CODES.has(countryCode.toUpperCase())
        ? "SOUTHERN"
        : "NORTHERN";
}
/**
 * Infers hemisphere from Bred In's country, falling back to the Stable's own
 * location, then a manual override always takes precedence when present
 * (`docs/products/stable.md` "Horse Details").
 */
export function inferHemisphere(input) {
    if (input.manualOverride !== null)
        return input.manualOverride;
    if (input.bredInCountryCode !== null) {
        return hemisphereForCountryCode(input.bredInCountryCode);
    }
    if (input.stableCountryCode !== null) {
        return hemisphereForCountryCode(input.stableCountryCode);
    }
    return "NORTHERN";
}
/**
 * Racing age turns over on Jan 1 (Northern) or Aug 1 (Southern) of the
 * horse's nominal racing-age year — never on the horse's literal birthday.
 * Month/day of birth are not needed for this calculation by design (year +
 * hemisphere fully determines racing age); an unset month/day is treated as
 * the start of the birth year, consistent with "day-to-day racing age never
 * needs more than year + hemisphere".
 */
export function computeRacingAge(dateOfBirth, hemisphere, asOf) {
    const turnoverMonthIndex = hemisphere === "NORTHERN" ? 0 : 7; // Jan=0, Aug=7
    const currentTurnoverYear = asOf.getUTCMonth() >= turnoverMonthIndex
        ? asOf.getUTCFullYear()
        : asOf.getUTCFullYear() - 1;
    const age = currentTurnoverYear - dateOfBirth.year;
    return Math.max(age, 0);
}
